import React, { useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import Swal from "sweetalert2";

import projectService from '../../../services/Projects/ProjectService';

export default function AddUserModal({ isOpen, onClose, projectId, onSuccess }) {
  const { keycloak } = useKeycloak();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#1e1e1e",
      color: "#fff",
      borderRadius: "8px",
      padding: "20px",
      width: "480px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      position: "relative",
    },
    header: {
      fontSize: "18px",
      fontWeight: "bold",
    },
    closeBtn: {
      position: "absolute",
      top: "10px",
      right: "12px",
      background: "transparent",
      border: "none",
      color: "#ccc",
      fontSize: "18px",
      cursor: "pointer",
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    label: {
      fontSize: "14px",
      color: "#e1e1e1",
      fontWeight: "400",
    },
    input: {
      borderRadius: "6px",
      border: "1px solid #314158",
      backgroundColor: "#121212",
      padding: "10px",
      color: "#fff",
      fontSize: "14px",
      outline: "none",
      width: "100%",
    },
    footer: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: "10px",
      gap: "10px",
    },
    cancelBtn: {
      backgroundColor: "transparent",
      border: "1px solid #333",
      color: "#ccc",
      padding: "8px 14px",
      borderRadius: "6px",
      cursor: "pointer",
    },
    submitBtn: (enabled) => ({
      backgroundColor: enabled ? "#8E51FF" : "#444",
      border: "none",
      color: enabled ? "#fff" : "#888",
      padding: "8px 14px",
      borderRadius: "6px",
      cursor: enabled ? "pointer" : "not-allowed",
      fontWeight: "bold",
    }),
  };

  const validateEmail = (value) => /^\S+@\S+\.\S+$/.test(value);

  const isValid = name.trim().length > 0 && validateEmail(email);

  const handleClose = () => {
    setName("");
    setEmail("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!projectId || !isValid || loading) return;

    setLoading(true);
    try {
      const result = await projectService.addUserToProject(projectId, name.trim(), email.trim(), keycloak.token);

      if (result?.temporaryPassword) {
        await Swal.fire({
          title: 'Usuário criado!',
          html: `Compartilhe a senha temporária com o usuário. Ele deverá alterá-la no primeiro acesso.<br/><br/>
            <div style="background:#121212;border:1px solid #314158;border-radius:6px;padding:10px;font-family:monospace;font-size:14px;word-break:break-all;">${result.temporaryPassword}</div>`,
          icon: 'success',
          confirmButtonText: 'Copiar senha e fechar',
          confirmButtonColor: '#8E51FF',
          background: '#27272A',
          color: '#fff',
          allowOutsideClick: false,
        }).then(() => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(result.temporaryPassword).catch(() => {});
          }
        });
      } else {
        Swal.fire({
          position: "top-end",
          title: "Usuário adicionado ao projeto!",
          showConfirmButton: false,
          timer: 2000,
          background: '#27272A',
          color: '#fff',
        });
      }

      if (typeof onSuccess === 'function') {
        onSuccess(result);
      }

      handleClose();
    } catch (error) {
      Swal.fire({
        position: "top-end",
        title: error.message || 'Erro ao adicionar usuário.',
        showConfirmButton: false,
        timer: 2200,
        background: '#27272A',
        color: '#fff',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button style={styles.closeBtn} onClick={handleClose}>✕</button>
        <h2 style={styles.header}>Adicionar usuário ao projeto</h2>

        <div style={styles.field}>
          <label style={styles.label}>Nome *</label>
          <input
            type="text"
            placeholder="Digite o nome do usuário"
            style={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>E-mail *</label>
          <input
            type="email"
            placeholder="Digite o e-mail do usuário"
            style={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={handleClose} disabled={loading}>
            Cancelar
          </button>

          <button
            style={styles.submitBtn(isValid && !loading)}
            disabled={!isValid || loading}
            onClick={handleSubmit}
          >
            {loading ? 'Adicionando...' : 'Adicionar usuário'}
          </button>
        </div>
      </div>
    </div>
  );
}
